import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, balanceEntries, fmtDate, fmtDateTime, ROLE_LABEL } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { useActions } from "../components/ActionDialogs";
import { EkAvansDialog } from "../components/EkAvansDialog";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  ArrowLeft, Plus, FileText, ExternalLink, Trash2, Download, Building2, User, Coins, Receipt, HandCoins, Wallet, StickyNote,
  ChevronDown, Link2Off, Pencil,
} from "lucide-react";
import { API, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReconciliationTab } from "../components/ReconciliationTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const CAT = "";

function TxnRow({ left, sub, amount, currency, negative, onDelete }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <div className="min-w-0">
        <p className="text-sm font-medium text-navy-900 truncate">{left}</p>
        <p className="text-xs text-slate-400 truncate">{sub}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={`font-mono text-sm font-semibold ${negative ? "text-brand-red" : "text-emerald-600"}`}>
          {negative ? "−" : "+"}{fmtMoney(amount, currency)}
        </span>
        {onDelete && <button onClick={onDelete} className="text-slate-300 hover:text-brand-red transition-colors"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="text-sm text-slate-400 py-10 text-center">{text}</p>;
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open } = useActions();
  const { user } = useAuth();
  const canUnlink = ["admin", "accountant"].includes(user?.role);
  const canCancel = ["admin", "accountant"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [muvOpen, setMuvOpen] = useState(false);
  const [muvVal, setMuvVal] = useState("");
  const [searchParams] = useSearchParams();
  const [ekOpen, setEkOpen] = useState(false);
  useEffect(() => { if (searchParams.get("ekavans") === "1") setEkOpen(true); }, [searchParams]);
  const [client, setClient] = useState(null);
  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [fees, setFees] = useState([]);
  const [collections, setCollections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [statement, setStatement] = useState({ entries: [], balances: {} });
  const [notes, setNotes] = useState([]);

  const load = useCallback(() => {
    api.get(`/clients/${id}`).then(({ data }) => setClient(data)).catch(() => navigate("/muvekkiller"));
    api.get(`/advances?client_id=${id}`).then(({ data }) => setAdvances(data));
    api.get(`/expenses?client_id=${id}`).then(({ data }) => setExpenses(data));
    api.get(`/fees?client_id=${id}`).then(({ data }) => setFees(data));
    api.get(`/collections?client_id=${id}`).then(({ data }) => setCollections(data));
    api.get(`/documents?client_id=${id}`).then(({ data }) => setDocuments(data));
    api.get(`/clients/${id}/statement`).then(({ data }) => setStatement(data));
    api.get(`/notes?client_id=${id}`).then(({ data }) => setNotes(data));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("shb:refresh", load); return () => window.removeEventListener("shb:refresh", load); }, [load]);

  const del = async (path, msg) => {
    try { await api.delete(path); toast.success(msg); load(); }
    catch { toast.error("İşlem başarısız"); }
  };

  const unlinkFee = async (colId) => {
    if (!window.confirm("Bu tahsilatın vekâlet ücreti bağlantısını kaldırmak istediğinize emin misiniz? Tahsilat silinmez, yalnızca vekâlet ücreti bağlantısı kaldırılır.")) return;
    try { await api.put(`/collections/${colId}/unlink-fee`); toast.success("Vekâlet ücreti bağlantısı kaldırıldı"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  const cancelTxn = async (path, msg, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    try { await api.delete(path); toast.success(msg); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  const toggleActive = async () => {
    const next = !(client.is_active !== false);
    const msg = next
      ? "Bu müvekkili tekrar aktif etmek istediğinize emin misiniz?"
      : "Bu müvekkili pasife almak istediğinize emin misiniz? Tüm kayıtlar (avans, masraf, tahsilat, vekâlet, belge, Drive klasörü) korunur; müvekkil hızlı seçimlerde görünmez.";
    if (!window.confirm(msg)) return;
    try { await api.put(`/clients/${id}/active?is_active=${next}`); toast.success(next ? "Müvekkil aktifleştirildi" : "Müvekkil pasife alındı"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  const saveMuvNo = async () => {
    try {
      await api.put(`/clients/${id}/client-no`, { client_no: muvVal });
      toast.success("Müvekkil numarası güncellendi");
      setMuvOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  const download = (fmt) => {
    window.open(`${API}/clients/${id}/statement/${fmt}`, "_blank");
  };

  if (!client) return <div className="py-20 text-center text-slate-400">Yükleniyor…</div>;

  const add = (type) => open(type, { clientId: id, onDone: load });

  return (
    <div className="space-y-5 animate-in-up">
      <button onClick={() => navigate("/muvekkiller")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy-900 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Müvekkiller
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-xl bg-navy-900 text-white flex items-center justify-center shrink-0">
            {client.client_type === "corporate" ? <Building2 className="w-6 h-6" /> : <User className="w-6 h-6" />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-navy-900">{client.name}</h1>
              <StatusBadge status={client.advance_status} />
              {client.is_active === false && (
                <span data-testid="client-passive-badge" className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-slate-200 text-slate-600">Pasif</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-slate-400 font-mono">{client.client_no}</p>
              {isAdmin && (
                <button data-testid="edit-muv-btn" title="Müvekkil numarasını değiştir"
                  onClick={() => { setMuvVal(client.client_no); setMuvOpen(true); }}
                  className="text-slate-300 hover:text-navy-900 transition-colors"><Pencil className="w-3 h-3" /></button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isAdmin && (
              <Button variant="outline" size="sm" className="rounded-xl" data-testid="toggle-active-btn" onClick={toggleActive}>
                {client.is_active === false ? "Aktife Al" : "Pasife Al"}
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-xl" data-testid="edit-client-header-btn"
              onClick={() => open("client", { editClient: client, onDone: load })}>Düzenle</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Metric label="Avans Bakiyesi" >{balanceEntries(client.balances).map(([c, v]) => <div key={c}>{fmtMoney(v, c)}</div>)}</Metric>
          <Metric label="Minimum Avans">{fmtMoney(client.min_advance_limit)}</Metric>
          <Metric label="Bekleyen Vekâlet">{balanceEntries(client.pending_fees).map(([c, v]) => <div key={c}>{fmtMoney(v, c)}</div>)}</Metric>
          <Metric label="Geciken Ödeme" danger>{balanceEntries(client.overdue_fees).map(([c, v]) => <div key={c}>{fmtMoney(v, c)}</div>)}</Metric>
        </div>
        <p className="text-xs text-slate-400 mt-3">Son ödeme tarihi: {client.last_payment_date ? fmtDate(client.last_payment_date) : "—"}</p>

        <div className="flex flex-wrap gap-2 mt-4">
          <QuickBtn onClick={() => add("advance")} icon={Coins} label="Avans" />
          <QuickBtn onClick={() => add("expense")} icon={Receipt} label="Masraf" />
          <QuickBtn onClick={() => add("collection")} icon={HandCoins} label="Tahsilat" />
          <QuickBtn onClick={() => add("fee")} icon={Wallet} label="Vekâlet Ücreti" />
          <QuickBtn onClick={() => add("document")} icon={FileText} label="Belge" />
          {client.advance_status !== "NORMAL" && (
            <QuickBtn onClick={() => setEkOpen(true)} icon={Wallet} label="Ek Avans Talep Et" />
          )}
        </div>
        <EkAvansDialog client={client} open={ekOpen} onClose={() => setEkOpen(false)} />
        <Dialog open={muvOpen} onOpenChange={setMuvOpen}>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading text-navy-900">Müvekkil Numarasını Değiştir</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <p className="text-xs text-slate-400">Örn: <span className="font-mono">MUV-0025</span> veya sadece <span className="font-mono">25</span>. Kullanılmış numaralar tekrar kullanılamaz.</p>
              <Input data-testid="muv-no-input" value={muvVal} onChange={(e) => setMuvVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveMuvNo()} placeholder="MUV-0025" className="h-11 rounded-xl font-mono" autoFocus />
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-xl" data-testid="muv-cancel-btn" onClick={() => setMuvOpen(false)}>Vazgeç</Button>
              <Button className="rounded-xl bg-navy-900 hover:bg-navy-800 text-white" data-testid="muv-save-btn" onClick={saveMuvNo}>Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="statement">
        <div className="overflow-x-auto no-scrollbar -mx-1 px-1">
          <TabsList className="bg-transparent p-0 h-auto gap-1 flex w-max">
            {[
              ["general", "Genel Bilgiler"], ["advances", "Masraf Avansları"], ["expenses", "Masraflar"],
              ["fees", "Vekâlet Ücretleri"], ["collections", "Tahsilatlar"], ["documents", "Belgeler"],
              ["statement", "Hesap Ekstresi"], ["reconciliation", "Mutabakat"], ["notes", "Notlar"],
            ].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-navy-900 data-[state=active]:text-white whitespace-nowrap">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="general" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
            <Info label="Müvekkil Türü" value={client.client_type === "corporate" ? "Tüzel Kişi" : "Gerçek Kişi"} />
            <Info label={client.client_type === "corporate" ? "Vergi No" : "T.C. Kimlik No"} value={client.tax_or_id_no} />
            <Info label="Telefon" value={client.phone} />
            <Info label="E-posta" value={client.email} />
            <Info label="Yetkili Kişi" value={client.contact_person} />
            <Info label="Adres" value={client.address} />
            <Info label="Açıklama" value={client.description} full />
          </div>
        </TabsContent>

        <TabsContent value="advances" className="mt-4">
          <ListCard onAdd={() => add("advance")} addLabel="Avans Ekle">
            {advances.length === 0 ? <EmptyState text="Avans kaydı yok." /> : advances.map((a) => (
              <TxnRow key={a.id} left={a.payment_method || "Avans"} sub={`${fmtDate(a.date)} · ${a.description || ""}`}
                amount={a.amount} currency={a.currency} onDelete={() => del(`/advances/${a.id}`, "Avans iptal edildi")} />
            ))}
          </ListCard>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          <ListCard onAdd={() => add("expense")} addLabel="Masraf Ekle">
            {expenses.length === 0 ? <EmptyState text="Masraf kaydı yok." /> : expenses.map((e) => (
              <TxnRow key={e.id} left={e.category} sub={`${fmtDate(e.date)} · ${e.description || ""}`}
                amount={e.amount} currency={e.currency} negative
                onDelete={canCancel ? () => cancelTxn(`/expenses/${e.id}`, "Masraf iptal edildi", "Bu masrafı iptal etmek istediğinize emin misiniz? Kayıt geçmişte kalır, aktif raporlardan ve avans bakiyesinden çıkar; belge Drive'da korunur.") : undefined} />
            ))}
          </ListCard>
        </TabsContent>

        <TabsContent value="fees" className="mt-4">
          <ListCard onAdd={() => add("fee")} addLabel="Vekâlet Ücreti Ekle">
            {fees.length === 0 ? <EmptyState text="Vekâlet ücreti kaydı yok." /> : fees.map((f) => (
              <FeeItem key={f.id} fee={f} linked={collections.filter((c) => c.fee_id === f.id)}
                canUnlink={canUnlink} onUnlink={unlinkFee} onDelete={() => del(`/fees/${f.id}`, "Kayıt iptal edildi")} />
            ))}
          </ListCard>
        </TabsContent>

        <TabsContent value="collections" className="mt-4">
          <ListCard onAdd={() => add("collection")} addLabel="Tahsilat Ekle">
            {collections.length === 0 ? <EmptyState text="Tahsilat kaydı yok." /> : collections.map((c) => {
              const linkedFee = c.fee_id ? fees.find((f) => f.id === c.fee_id) : null;
              return (
                <div key={c.id} className="flex items-center justify-between py-3 border-b border-slate-100" data-testid={`collection-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-navy-900 truncate">{c.payment_method || "Tahsilat"}</p>
                    <p className="text-xs text-slate-400 truncate">{fmtDate(c.date)} · {c.description || ""}</p>
                    {c.fee_id && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-navy-900 bg-slate-100 px-2 py-0.5 rounded-full">
                        <Wallet className="w-3 h-3" /> {linkedFee ? (linkedFee.description || "Vekâlet ücreti") : "Vekâlet ücretine bağlı"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono text-sm font-semibold text-emerald-600">+{fmtMoney(c.amount, c.currency)}</span>
                    {c.fee_id && canUnlink && (
                      <button onClick={() => unlinkFee(c.id)} title="Vekâlet ücreti bağını kaldır" data-testid={`collection-unlink-${c.id}`}
                        className="text-slate-300 hover:text-amber-600 transition-colors"><Link2Off className="w-4 h-4" /></button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancelTxn(`/collections/${c.id}`, "Tahsilat iptal edildi", "Bu tahsilatı iptal etmek istediğinize emin misiniz? Kayıt geçmişte kalır, raporlardan çıkar; bağlı vekâlet ücreti yeniden hesaplanır; dekont Drive'da korunur.")} title="İptal Et" data-testid={`collection-cancel-${c.id}`} className="text-slate-300 hover:text-brand-red transition-colors"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </ListCard>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <ListCard onAdd={() => add("document")} addLabel="Belge Ekle">
            {documents.length === 0 ? <EmptyState text="Belge yok." /> : documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 py-3 border-b border-slate-100">
                <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-navy-900" /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy-900 truncate">{doc.file_name}</p>
                  <p className="text-xs text-slate-400">{fmtDateTime(doc.upload_date)} · {doc.storage === "drive" ? "Google Drive" : "Yerel"}</p>
                </div>
                {doc.web_link
                  ? <a href={doc.web_link} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-navy-900"><ExternalLink className="w-4 h-4" /></a>
                  : <a href={`${API}/documents/${doc.id}/download`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-navy-900"><Download className="w-4 h-4" /></a>}
                <button onClick={() => del(`/documents/${doc.id}`, "Belge silindi")} className="text-slate-300 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </ListCard>
        </TabsContent>

        <TabsContent value="statement" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-navy-900">Avans Hesap Ekstresi</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-lg" data-testid="stmt-pdf-btn" onClick={() => download("pdf")}><Download className="w-4 h-4 mr-1" />PDF</Button>
                <Button size="sm" variant="outline" className="rounded-lg" data-testid="stmt-excel-btn" onClick={() => download("excel")}><Download className="w-4 h-4 mr-1" />Excel</Button>
              </div>
            </div>
            {statement.entries.length === 0 ? <EmptyState text="Hareket yok." /> : (
              <div>
                {statement.entries.map((e, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy-900">{e.type_label}</p>
                      <p className="text-xs text-slate-400 truncate">{fmtDate(e.date)} · {e.description || "—"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {Number(e.credit) > 0 && <p className="font-mono text-sm text-emerald-600">+{fmtMoney(e.credit, e.currency)}</p>}
                      {Number(e.debit) > 0 && <p className="font-mono text-sm text-brand-red">−{fmtMoney(e.debit, e.currency)}</p>}
                      <p className="font-mono text-xs text-slate-500">Bakiye: {fmtMoney(e.balance, e.currency)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-4">
          <ReconciliationTab clientId={id} clientNo={client.client_no} />
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <ListCard onAdd={() => add("note")} addLabel="Not Ekle">
            {notes.length === 0 ? <EmptyState text="Not yok." /> : notes.map((n) => (
              <div key={n.id} className="flex items-start gap-3 py-3 border-b border-slate-100">
                <StickyNote className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-navy-900">{n.text}</p>
                  <p className="text-xs text-slate-400">{n.created_by?.name} · {fmtDateTime(n.created_at)}</p>
                </div>
                <button onClick={() => del(`/notes/${n.id}`, "Not silindi")} className="text-slate-300 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </ListCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Metric({ label, children, danger }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
      <div className={`text-sm font-mono font-semibold ${danger ? "text-brand-red" : "text-navy-900"}`}>{children}</div>
    </div>
  );
}
function Info({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-navy-900">{value || "—"}</p>
    </div>
  );
}
function QuickBtn({ onClick, icon: Icon, label }) {
  return (
    <button onClick={onClick} data-testid={`detail-add-${label}`}
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm text-navy-900 hover:bg-slate-50 active:scale-[0.98] transition-all">
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
function ListCard({ children, onAdd, addLabel }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex justify-end mb-1">
        <Button size="sm" variant="ghost" className="rounded-lg text-navy-900" onClick={onAdd}><Plus className="w-4 h-4 mr-1" />{addLabel}</Button>
      </div>
      {children}
    </div>
  );
}
function FeeStatus({ status }) {
  const map = {
    collected: ["ÖDENDİ", "bg-emerald-100 text-emerald-700"],
    overdue: ["GECİKTİ", "bg-brand-redbg text-brand-red"],
    partial: ["KISMİ ÖDENDİ", "bg-amber-100 text-amber-800"],
    pending: ["BEKLİYOR", "bg-slate-100 text-slate-600"],
  };
  const [l, c] = map[status] || map.pending;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${c}`}>{l}</span>;
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <div className="text-sm font-mono font-semibold text-navy-900">{value}</div>
    </div>
  );
}
function FeeItem({ fee, linked, canUnlink, onUnlink, onDelete }) {
  const [openRow, setOpenRow] = useState(false);
  const remNum = Number(fee.remaining);
  return (
    <div className="border-b border-slate-100" data-testid={`fee-row-${fee.id}`}>
      <div className="flex items-center justify-between py-3">
        <button onClick={() => setOpenRow((v) => !v)} className="flex items-center gap-2 min-w-0 text-left" data-testid={`fee-toggle-${fee.id}`}>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${openRow ? "rotate-180" : ""}`} />
          <span className="min-w-0">
            <p className="text-sm font-medium text-navy-900 truncate">{fee.description || "Vekâlet ücreti"}</p>
            <p className="text-xs text-slate-400">Vade {fmtDate(fee.due_date)} · {remNum < 0 ? `Fazla Ödeme ${fmtMoney(-remNum, fee.currency)}` : `Kalan ${fmtMoney(fee.remaining, fee.currency)}`}</p>
          </span>
        </button>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {remNum < 0 && (
            <span data-testid={`fee-overpay-${fee.id}`} className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-amber-100 text-amber-800">
              Fazla Ödeme +{fmtMoney(-remNum, fee.currency)}
            </span>
          )}
          <FeeStatus status={fee.status} />
          <span className="font-mono text-sm font-semibold text-navy-900">{fmtMoney(fee.amount, fee.currency)}</span>
          <button onClick={onDelete} className="text-slate-300 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {openRow && (
        <div className="pb-4 pl-6 pr-1 space-y-3" data-testid={`fee-detail-${fee.id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MiniStat label="Toplam" value={fmtMoney(fee.amount, fee.currency)} />
            <MiniStat label="Tahsil Edilen" value={fmtMoney(fee.collected, fee.currency)} />
            <MiniStat label="Kalan" value={fmtMoney(remNum < 0 ? 0 : fee.remaining, fee.currency)} />
            <MiniStat label="Durum" value={<FeeStatus status={fee.status} />} />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Bağlı Tahsilatlar</p>
            {linked.length === 0 ? (
              <p className="text-xs text-slate-400 py-3">Bu vekâlet ücretine bağlı tahsilat yok.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {linked.map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-2" data-testid={`fee-linked-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-navy-900 truncate">{fmtMoney(c.amount, c.currency)} · {c.payment_method || "Tahsilat"}</p>
                      <p className="text-xs text-slate-400 truncate">{fmtDate(c.date)} · {c.description || "—"}</p>
                    </div>
                    {canUnlink && (
                      <button onClick={() => onUnlink(c.id)} title="Bağı kaldır" data-testid={`fee-linked-unlink-${c.id}`}
                        className="text-slate-300 hover:text-amber-600 transition-colors shrink-0"><Link2Off className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
